/**
 * Shared helpers for displaying and ordering student names (LEARN-855).
 *
 * Everywhere we list students we want one consistent behaviour:
 *  - display each name as "Last, First"
 *  - order the list by last name, then first name, case-insensitively
 *
 * Names are used exactly as they come from the roster (no client-side
 * splitting), so multi-part last names such as "Chacon Olay" stay intact.
 */
export type StudentName = {
  first_name: string;
  last_name: string;
};

/**
 * Compare two students by last name, then first name to break ties.
 * Case-insensitive. Use with Array.prototype.sort.
 */
export const compareStudentsByName = (a: StudentName, b: StudentName): number =>
  (a.last_name ?? '').localeCompare(b.last_name ?? '', undefined, {
    sensitivity: 'accent',
  }) ||
  (a.first_name ?? '').localeCompare(b.first_name ?? '', undefined, {
    sensitivity: 'accent',
  });

/**
 * Return a new list ordered by last name, then first name. Does not mutate
 * the input array.
 */
export const orderStudentsByName = <T extends StudentName>(students: T[]): T[] =>
  [...students].sort(compareStudentsByName);

/**
 * Format a student's name as "Last, First".
 * Falls back to whichever part exists when one is missing.
 */
export const formatStudentName = (student: StudentName): string => {
  const last = (student.last_name ?? '').trim();
  const first = (student.first_name ?? '').trim();
  if (last && first) {
    return `${last}, ${first}`;
  }
  return last || first;
};
